import type { Step } from '@blooq/flow'
import { IonButton, IonIcon } from '@ionic/react'
import {
  callOutline,
  calendarOutline,
  createOutline,
  homeOutline,
  shareSocialOutline,
  warningOutline,
} from 'ionicons/icons'
import type { ReactNode } from 'react'

import type { StepView } from '../../lib/api'
import { canShare, share, tap } from '../../lib/native'
import { webBase } from '../../lib/tenant'

import type { DockProps, Send } from './StepDock'

type TerminalStep = Extract<Step, { type: 'terminal' }>

/**
 * A terminal that is a MESSAGE rather than a screen.
 *
 * An FAQ answer, "we couldn't find that booking", "enquiry received": the end
 * of the branch is a sentence, and the things to do next are two or three
 * links. Drawn as a bubble with its actions attached underneath — the shape a
 * messaging app gives a reply with buttons — rather than as an empty prompt
 * over a dock full of full-width buttons.
 *
 * Decided from what the result CARRIES, not from the step's id. A reference
 * needs a ticket and a payment needs a status line, so those stay in the dock;
 * the emergency number stays there too, because a red button the width of the
 * screen is the one place hierarchy is a safety property.
 */
export function isMessageTerminal(view: StepView): boolean {
  if (view.step.type !== 'terminal') return false
  const result = view.result ?? {}
  const show = view.step.show ?? []
  if (show.includes('callEmergency')) return false
  if (result.ref) return false
  if (result.paymentStatus && result.paymentStatus !== 'not_required') return false
  return true
}

/**
 * The text of a message-shaped terminal.
 *
 * The step's own prompt where it has one. A terminal with NO prompt is one
 * whose reply is data — the FAQ answer — and the runtime hands that over in
 * the result. It is the business's own stored text, rendered as plain text:
 * nothing the model writes reaches a customer, it only picks which row.
 *
 * The prompt has to win where there is one. Answers live in the session, so
 * a stored answer from a question asked earlier is still in the result on
 * "call us" three steps later, and it must not be read out again there.
 */
export function terminalText(view: StepView): string {
  const prompt = 'prompt' in view.step ? view.step.prompt : ''
  if (prompt) return prompt
  const answer = view.result?.faqAnswer
  return answer ? String(answer) : ''
}

/**
 * The end of a branch, and whatever the flow says to offer there.
 *
 * `step.show` is set in canonical.ts — a booking ends with a reference and a
 * calendar link, an urgent enquiry ends with a phone number — so this reads the
 * list rather than deciding it from the step's id.
 */
export function TerminalDock({ view, busy, onSend }: DockProps) {
  const result = view.result ?? {}
  const ref = result.ref ? String(result.ref) : ''

  return (
    <>

      {/*
        Where the payment stands.

        A customer who prepaid needs to see that it has not been confirmed yet —
        otherwise "submitted" and "verified" look identical and they arrive
        expecting the desk to already know.
      */}
      {result.paymentStatus && result.paymentStatus !== 'not_required' ? (
        <div className="blooq-card" style={{ padding: '10px 12px' }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
            {result.paymentStatus === 'submitted'
              ? 'Payment screenshot received. It will be confirmed before your visit.'
              : result.paymentStatus === 'verified'
                ? 'Payment confirmed ✓'
                : 'Payment not received yet — you can pay in person.'}
          </p>
        </div>
      ) : null}

      {(view.step as TerminalStep).show?.includes('appointmentRef') && ref ? (
        <div className="blooq-ticket">
          <div style={{ fontSize: 12, color: 'var(--blooq-muted)' }}>Your appointment ID</div>
          <div className="blooq-ticket__ref">{ref}</div>
          <p className="blooq-meta">
            Keep this. You’ll need it and your mobile number to change or cancel the appointment.
          </p>
        </div>
      ) : null}

      <TerminalActions view={view} busy={busy} onSend={onSend} />
    </>
  )
}

/**
 * What the flow says to offer at the end, as a list.
 *
 * `inline` draws them as rows attached under a message bubble; otherwise they
 * are the dock's block buttons. Same list, same order, same handlers — only
 * the shape changes.
 */
export function TerminalActions({
  view,
  busy,
  onSend,
  inline,
}: {
  view: StepView
  busy: boolean
  onSend: Send
  inline?: boolean
}) {
  const step = view.step as TerminalStep
  const show = step.show ?? []
  const result = view.result ?? {}
  const urgent = step.id === 'urgent'
  const ref = result.ref ? String(result.ref) : ''

  const Action = inline ? InlineAction : DockAction

  return (
    <div className={inline ? 'blooq-actions' : 'blooq-walk'}>
      {/* Emergency first and visually dominant in the urgent branch — the one
          place where hierarchy is a safety property rather than a style call. */}
      {show.includes('callEmergency') ? (
        <IonButton
          expand="block"
          color="danger"
          href={`tel:${view.tenant.emergencyNumber}`}
          style={urgent ? { '--min-height': '52px', fontSize: 15 } : undefined}
        >
          <IonIcon slot="start" icon={warningOutline} />
          Call emergency · {view.tenant.emergencyNumber}
        </IonButton>
      ) : null}

      {/*
        The reference, into whatever the customer uses to remember things.

        On a phone that is usually their own WhatsApp, which is where a booking
        ID is actually useful and somewhere we cannot put it ourselves. Offered
        only where a share sheet exists, so nothing here is a button that does
        nothing.
      */}
      {ref && canShare() ? (
        <Action
          icon={shareSocialOutline}
          disabled={busy}
          onClick={() =>
            void share({
              title: view.tenant.businessName,
              text: `Appointment ${ref} at ${view.tenant.businessName}.`,
            })
          }
        >
          Share this booking
        </Action>
      ) : null}

      {/*
        The .ics is a document served by the web app, not an API response, so it
        opens rather than being fetched. `target=_blank` is what sends it to the
        system browser inside the native shell, where a WebView cannot hand a
        file to the calendar itself.
      */}
      {show.includes('addToCalendar') && result.appointmentId ? (
        <Action
          icon={calendarOutline}
          href={`${webBase}/c/${String(result.slug ?? '')}/ics/${String(result.appointmentId)}`}
          external
        >
          Add to calendar
        </Action>
      ) : null}

      {show.includes('callBusiness') ? (
        <Action icon={callOutline} href={`tel:${view.tenant.phone}`}>
          Call us
        </Action>
      ) : null}

      {show.includes('manageBooking') && step.manage ? (
        <Action
          icon={createOutline}
          disabled={busy}
          // A restart that lands on the manage branch, which the menu no
          // longer lists. The server takes the target from the step; this
          // only says which of the two restarts was meant.
          onClick={() => onSend({ __restart: true, __manage: true })}
        >
          Change or cancel this booking
        </Action>
      ) : null}

      {show.includes('backToMenu') ? (
        <Action
          icon={homeOutline}
          quiet
          disabled={busy}
          // Restart, not back: this clears the answers, so the next person at a
          // shared counter tablet does not inherit the last one's details.
          onClick={() => onSend({ __restart: true })}
        >
          Back to menu
        </Action>
      ) : null}
    </div>
  )
}

interface ActionProps {
  icon: string
  children: ReactNode
  href?: string
  /** Opens in the system browser inside the native shell — see the .ics note. */
  external?: boolean
  disabled?: boolean
  onClick?: () => void
  /** The least important option, drawn without a fill in the dock. */
  quiet?: boolean
}

function DockAction({ icon, children, href, external, disabled, onClick, quiet }: ActionProps) {
  return (
    <IonButton
      expand="block"
      fill={quiet ? 'clear' : 'outline'}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <IonIcon slot="start" icon={icon} />
      {children}
    </IonButton>
  )
}

/**
 * One row under a message. A link where it opens something, a button where it
 * does something — the same distinction the dock draws, kept so a `tel:` row
 * is a real link the OS can hand to the dialler.
 */
function InlineAction({ icon, children, href, external, disabled, onClick }: ActionProps) {
  const inner = (
    <>
      <IonIcon icon={icon} aria-hidden="true" />
      <span>{children}</span>
    </>
  )
  if (href) {
    return (
      <a
        className="blooq-actions__row"
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}
        onClick={() => tap()}
      >
        {inner}
      </a>
    )
  }
  return (
    <button
      type="button"
      className="blooq-actions__row"
      disabled={disabled}
      onClick={() => {
        tap()
        onClick?.()
      }}
    >
      {inner}
    </button>
  )
}
